export const FECHAMENTO_CREDENCIAL = Object.freeze({
    RECUSADA_NO_HANDSHAKE: 1008,
    SESSAO_REVOGADA: 4401,
});

/**
 * Código local (o servidor nunca o envia) para o socket que o próprio cliente deu por morto: a conexão
 * não fechou, mas o servidor parou de responder. Não é credencial recusada, então reconecta.
 */
export const FECHAMENTO_SEM_RESPOSTA = 4000;

export const ESTADOS = Object.freeze({
    DESCONECTADO: 'desconectado',
    CONECTANDO: 'conectando',
    AUTENTICANDO: 'autenticando',
    PRONTO: 'pronto',
    SESSAO_INVALIDA: 'sessao_invalida',
});

export class TabletNetworkManager {
    /**
     * @param {string} serverUrl base do WebSocket, ex.: "ws://localhost:8000/"
     * @param {string} tabletId identificador do aparelho (telemetria, não credencial)
     * @param {object} [opcoes]
     * @param {() => (string|null)} [opcoes.obterToken] fonte do token; sem ele
     *   o handshake não tem o que enviar.
     * @param {(url: string) => WebSocket} [opcoes.criarWebSocket] injetado no teste.
     * @param {(fn: Function, ms: number) => any} [opcoes.agendar] injetado para
     *   provar o backoff sem esperar em tempo real.
     * @param {(id: any) => void} [opcoes.cancelar]
     * @param {() => number} [opcoes.aleatorio] jitter determinístico no teste.
     * @param {number} [opcoes.pongTimeoutMs] quanto esperar qualquer resposta depois de um ping antes de
     *   dar a conexão por morta (rede que cai sem fechar o socket).
     * @param {number} [opcoes.conexaoTimeoutMs] quanto esperar a conexão chegar a `pronto` depois do `connect()`.
     * @param {{janela?: object, documento?: object}} [opcoes.ambiente] onde ouvir `online` e a volta da aba
     *   ao primeiro plano; injetado no teste. Sem ele usa `window` e `document`, se existirem.
     */
    constructor(serverUrl, tabletId, opcoes = {}) {
        this.serverUrl = String(serverUrl).replace(/\/+$/, '');
        this.tabletId = tabletId;
        this.ws = null;
        this.estado = ESTADOS.DESCONECTADO;

        this.reconnectBaseMs = opcoes.reconnectBaseMs ?? 1000;
        this.reconnectMaxMs = opcoes.reconnectMaxMs ?? 30000;
        this.pingIntervalDelay = opcoes.pingIntervalDelay ?? 10000;
        // O servidor derruba quem passa de 15 s calado e o ping é de 10 s: 5 s sem resposta é queda.
        this.pongTimeoutMs = opcoes.pongTimeoutMs ?? 5000;
        this.conexaoTimeoutMs = opcoes.conexaoTimeoutMs ?? 15000;
        this._ambiente = opcoes.ambiente ?? { janela: globalThis.window, documento: globalThis.document };
        this._ambienteLigado = false;

        this._obterToken = opcoes.obterToken ?? (() => null);
        this._criarWebSocket = opcoes.criarWebSocket ?? ((url) => new WebSocket(url));
        this._agendar = opcoes.agendar ?? ((fn, ms) => setTimeout(fn, ms));
        this._cancelar = opcoes.cancelar ?? ((id) => clearTimeout(id));
        this._aleatorio = opcoes.aleatorio ?? Math.random;

        this.tentativas = 0;
        this.pingTimer = null;
        this.reconnectTimer = null;
        this.pongTimer = null;
        this.conexaoTimer = null;
        this._encerradoDePropósito = false;

        /** @type {Map<string, Set<Function>>} */
        this._ouvintes = new Map();
    }

    on(evento, callback) {
        if (!this._ouvintes.has(evento)) this._ouvintes.set(evento, new Set());
        this._ouvintes.get(evento).add(callback);
        return () => this._ouvintes.get(evento)?.delete(callback);
    }

    _emitir(evento, dados) {
        for (const callback of this._ouvintes.get(evento) ?? []) {
            try {
                callback(dados);
            } catch (erro) {
                console.error(`[Network] Ouvinte de '${evento}' falhou:`, erro);
            }
        }
    }

    _mudarEstado(novo, detalhe) {
        if (this.estado === novo) return;
        this.estado = novo;
        this._emitir('estado', { estado: novo, detalhe });
    }

    get url() {
        return `${this.serverUrl}/api/v1/ws/play/${this.tabletId}`;
    }

    connect() {
        if (this.estado === ESTADOS.SESSAO_INVALIDA) {
            console.warn('[Network] Sessão inválida: é preciso autenticar de novo antes de conectar.');
            return;
        }
        // Já há socket conectando ou aberto (um `sessao:iniciada` durante a espera do retry, por exemplo):
        // um segundo `connect()` deixaria dois sockets vivos para o mesmo tablet.
        if (this.ws && (this.ws.readyState === 0 || this.ws.readyState === 1)) return;
        this._cancelarReconexao();
        this._encerradoDePropósito = false;
        this._observarAmbiente();
        this._mudarEstado(ESTADOS.CONECTANDO);
        console.log(`[Network] Conectando tablet ${this.tabletId}...`);

        this.ws = this._criarWebSocket(this.url);
        // Conexão que nunca chega a `pronto` (rede morta no meio do handshake) também é queda.
        this._cancelarTemporizador('conexaoTimer');
        this.conexaoTimer = this._agendar(() => {
            this.conexaoTimer = null;
            if (this.estado !== ESTADOS.PRONTO) this._darPorMorta('conexao_sem_resposta');
        }, this.conexaoTimeoutMs);

        this.ws.onopen = () => this.autenticar();
        this.ws.onmessage = (evento) => this._aoReceber(evento);
        this.ws.onclose = (evento) => this._aoFechar(evento);
        this.ws.onerror = (erro) => {
            console.error('[Network] Erro no socket:', erro);
        };
    }

    autenticar() {
        const token = this._obterToken();
        if (!token) {
            console.error('[Network] Sem token: faça login antes de conectar.');
            this._mudarEstado(ESTADOS.SESSAO_INVALIDA, 'token_ausente');
            this._emitir('sessao_invalida', { motivo: 'token_ausente' });
            this.ws?.close();
            return;
        }

        this._mudarEstado(ESTADOS.AUTENTICANDO);
        this._enviarBruto({ type: 'auth', token });
    }

    _aoReceber(evento) {
        // Qualquer mensagem prova que o servidor está lá, não só o `pong`.
        this._cancelarTemporizador('pongTimer');
        let mensagem;
        try {
            mensagem = JSON.parse(evento.data);
        } catch (erro) {
            console.error('[Network] Mensagem ilegível do servidor:', erro);
            return;
        }
        this.handleIncomingMessage(mensagem);
    }

    handleIncomingMessage(mensagem) {
        switch (mensagem.type) {
            case 'auth_confirmada':
                this._cancelarTemporizador('conexaoTimer');
                this.tentativas = 0;
                this.sessao = {
                    papel: mensagem.papel,
                    turmaId: mensagem.turma_id,
                    grupoId: mensagem.grupo_id,
                };
                this._mudarEstado(ESTADOS.PRONTO);
                this.startHeartbeat();
                console.log(`[Network] Autenticado como ${mensagem.papel} (grupo ${mensagem.grupo_id}).`);
                this._emitir('pronto', this.sessao);
                break;

            case 'auth_erro':
                console.error(`[Network] Autenticação recusada: ${mensagem.motivo}`);
                this._mudarEstado(ESTADOS.SESSAO_INVALIDA, 'auth_erro');
                // Token salvo vencido ou revogado: sem este aviso o tablet ficava preso no Aguarde.
                this._emitir('sessao_invalida', { motivo: 'auth_erro' });
                break;

            case 'inscricao_confirmada':
                this._emitir('sala_inscrita', mensagem);
                break;

            case 'inscricao_recusada':
            case 'sala_lotada':
                console.warn(`[Network] Sala ${mensagem.room_id} recusada: ${mensagem.motivo ?? mensagem.type}`);
                this._emitir('sala_recusada', mensagem);
                break;

            case 'pong':
                break;

            default:
                this._emitir('mensagem', mensagem);
        }
    }

    _aoFechar(evento) {
        const codigo = evento?.code;
        this.stopHeartbeat();
        this._cancelarTemporizador('conexaoTimer');
        this.ws = null;

        if (this.estado === ESTADOS.SESSAO_INVALIDA) return;

        if (this._encerradoDePropósito || codigo === 1000) {
            this._mudarEstado(ESTADOS.DESCONECTADO);
            return;
        }

        if (
            codigo === FECHAMENTO_CREDENCIAL.RECUSADA_NO_HANDSHAKE ||
            codigo === FECHAMENTO_CREDENCIAL.SESSAO_REVOGADA
        ) {
            const motivo = codigo === FECHAMENTO_CREDENCIAL.SESSAO_REVOGADA
                ? 'sessao_revogada'
                : 'credencial_recusada';
            console.error(`[Network] Fechado com ${codigo} (${motivo}). Não reconecta: precisa de novo login.`);
            this._mudarEstado(ESTADOS.SESSAO_INVALIDA, motivo);
            this._emitir('sessao_invalida', { motivo, codigo });
            return;
        }

        this._mudarEstado(ESTADOS.DESCONECTADO, codigo);
        this._agendarReconexao(codigo);
    }

    _atrasoDaReconexao() {
        const teto = Math.min(this.reconnectBaseMs * 2 ** this.tentativas, this.reconnectMaxMs);
        return Math.round(teto / 2 + this._aleatorio() * (teto / 2));
    }

    _agendarReconexao(codigo) {
        const atraso = this._atrasoDaReconexao();
        this.tentativas += 1;
        console.warn(`[Network] Conexão caiu (código ${codigo}). Reconectando em ${atraso}ms.`);
        this.reconnectTimer = this._agendar(() => {
            this.reconnectTimer = null;
            this.connect();
        }, atraso);
    }

    desconectar() {
        this._encerradoDePropósito = true;
        this.stopHeartbeat();
        this._cancelarTemporizador('conexaoTimer');
        this._cancelarReconexao();
        this.ws?.close(1000);
        this.ws = null;
        this._mudarEstado(ESTADOS.DESCONECTADO);
    }

    _enviarBruto(payload) {
        if (!this.ws || this.ws.readyState !== 1) return false;
        this.ws.send(JSON.stringify(payload));
        return true;
    }

    enviar(payload) {
        if (this.estado !== ESTADOS.PRONTO) return false;
        return this._enviarBruto(payload);
    }

    inscreverNaSala(roomId) {
        return this.enviar({ type: 'inscricao_sala', room_id: roomId });
    }

    sendAction(actionType, payloadData) {
        return this.enviar({
            tablet_id: this.tabletId,
            type: actionType,
            data: payloadData,
            timestamp: Date.now(),
        });
    }

    startHeartbeat() {
        this.stopHeartbeat();
        this.pingTimer = this._agendar(function repetir() {
            this._pingar();
            this.pingTimer = this._agendar(repetir.bind(this), this.pingIntervalDelay);
        }.bind(this), this.pingIntervalDelay);
    }

    stopHeartbeat() {
        this._cancelarTemporizador('pingTimer');
        this._cancelarTemporizador('pongTimer');
    }

    /**
     * Manda um ping e arma o prazo da resposta. Sem resposta a tempo, a conexão está morta mesmo que o
     * navegador ainda a mostre aberta (Wi-Fi que cai sem fechar o socket): derruba e reconecta.
     */
    _pingar() {
        if (!this._enviarBruto({ type: 'ping', tablet_id: this.tabletId })) return;
        if (this.pongTimer !== null) return;
        this.pongTimer = this._agendar(() => {
            this.pongTimer = null;
            this._darPorMorta('pong_sem_resposta');
        }, this.pongTimeoutMs);
    }

    /**
     * A rede voltou ou o app voltou ao primeiro plano. Parado em backoff, reconecta já; pronto, testa a
     * conexão com um ping (o socket pode ter morrido enquanto a tela estava apagada).
     */
    sondar() {
        if (this._encerradoDePropósito || this.estado === ESTADOS.SESSAO_INVALIDA) return;
        if (this.reconnectTimer !== null) {
            console.log('[Network] Rede de volta: reconectando sem esperar o backoff.');
            this._cancelarReconexao();
            this.tentativas = 0;
            this.connect();
            return;
        }
        if (this.estado === ESTADOS.PRONTO) this._pingar();
    }

    /** Descarta o socket que não responde e segue o mesmo caminho de uma queda: estado, backoff, reconexão. */
    _darPorMorta(motivo) {
        const morto = this.ws;
        if (!morto) return;
        console.warn(`[Network] Conexão sem resposta (${motivo}). Derrubando e reconectando.`);
        // O `onclose` de um socket meio aberto pode levar minutos: o fechamento é tratado aqui, uma vez só.
        morto.onopen = morto.onmessage = morto.onclose = morto.onerror = null;
        try { morto.close(); } catch { /* já fechado */ }
        this._aoFechar({ code: FECHAMENTO_SEM_RESPOSTA });
    }

    _cancelarTemporizador(nome) {
        if (this[nome] === null) return;
        this._cancelar(this[nome]);
        this[nome] = null;
    }

    _cancelarReconexao() {
        this._cancelarTemporizador('reconnectTimer');
    }

    _observarAmbiente() {
        if (this._ambienteLigado) return;
        this._ambienteLigado = true;
        const { janela, documento } = this._ambiente;
        janela?.addEventListener?.('online', () => this.sondar());
        documento?.addEventListener?.('visibilitychange', () => {
            if (documento.visibilityState !== 'hidden') this.sondar();
        });
    }
}
